import { inject, injectWithTransform, singleton } from "tsyringe";
import { Logger, LoggerTransformer } from "./logger";
import { COOKIES, WINDOW } from "../symbols";
import { countriesRequiringConsents } from "./countries-requiring-consents";

export interface GeoData {
  region?: string;
  country?: string;
  continent?: string;
}

@singleton()
export class Geo {
  private geoData: GeoData;

  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WINDOW) private window: Window,
    @injectWithTransform(Logger, LoggerTransformer, "Geo")
    private readonly logger: Logger,
  ) {}

  get(): GeoData {
    if (this.geoData) {
      return this.geoData;
    }

    this.geoData = this.getGeoData();
    return this.geoData;
  }

  set(geoData: GeoData): void {
    this.geoData = geoData;
  }

  private getGeoData(): GeoData {
    const cookieData = this.getGeoDataFromCookie();
    if (Object.keys(cookieData).length) {
      return cookieData;
    }

    return this.getGeoDataFromOneTrust();
  }

  private getGeoDataFromCookie(): GeoData {
    const geoCookie = this.cookiesManager.get("Geo");

    if (!geoCookie) {
      return {};
    }
    const jsonData = decodeURIComponent(geoCookie);

    try {
      return JSON.parse(jsonData) as GeoData;
    } catch (e) {
      this.logger.error("Invalid JSON in the cookie", e);
      return {};
    }
  }

  private getGeoDataFromOneTrust(): GeoData {
    if (this.window.OneTrust && this.window.OneTrust.getGeolocationData()) {
      return {
        country: this.window.OneTrust.getGeolocationData().country,
      };
    }

    return {};
  }

  getCountryCode(): string | undefined {
    return this.get()?.country?.toLowerCase();
  }

  getRegionCode(): string | undefined {
    return this.get()?.region?.toLowerCase();
  }

  getContinentCode(): string | undefined {
    return this.get()?.continent?.toLowerCase();
  }

  isGdprCountry(): boolean {
    return countriesRequiringConsents.includes(this.getCountryCode());
  }
}
